import EmailRecipientTag from "components/Tag/ResourceTargetTag/EmailRecipientTag";
import { Flex } from "@chakra-ui/react";
import { EmailTemplate } from "features/Resources/EmailTemplate/models";
import DisplayResourceTargetsColumn from "features/Resources/SharedTemplate/Components/DisplayResourceTargetsColumn";
import ResourceTargetAvatar from "features/Resources/SharedTemplate/Components/ResourceTargetAvatar";
import { Email } from "../models";
import { useEmailRecipientDetails } from "../hooks";
import { EmailRecipient } from "../models/EmailRecipient";

function EmailRecipientAvatar({ recipient }: { recipient: EmailRecipient }) {
  const { displayName, src, role } = useEmailRecipientDetails(recipient);
  return <ResourceTargetAvatar name={displayName} src={src} roleName={role} />;
}

export function DisplayRecipientsColumn({ recipients }: { recipients?: EmailRecipient[] }) {
  return (
    <Flex align="center" gap="1">
      <DisplayResourceTargetsColumn<EmailRecipient>
        items={recipients || []}
        renderTag={(recipient: EmailRecipient) => (
          <EmailRecipientTag
            key={recipient.id || ("customEmail" in recipient ? recipient.customEmail : "unknown")}
            recipient={recipient}
            disabled
          />
        )}
        renderAvatar={(recipient: EmailRecipient) => <EmailRecipientAvatar recipient={recipient} />}
        getKey={(recipient: EmailRecipient) => recipient.id || ("customEmail" in recipient ? recipient.customEmail : "unknown")}
        emptyLabel="No Recipients"
      />
    </Flex>
  );
}

export function DisplayRecipients({ email }: { email: Email | EmailTemplate }) {
  const to = email?.to ?? [];
  const cc = email?.cc ?? [];
  const bcc = email?.bcc ?? [];

  // Flatten all recipients for easier logic
  const allRecipients = [...to, ...cc, ...bcc];

  return <DisplayRecipientsColumn recipients={allRecipients} />;
}

export function DisplayRecipientsValue({ email }: { email: Email | EmailTemplate }) {
  const to = email?.to ?? [];
  const cc = email?.cc ?? [];
  const bcc = email?.bcc ?? [];

  // Flatten all recipients for easier logic
  const allRecipients = [...to, ...cc, ...bcc];

  // Determine the primary recipient to show first
  const primaryRecipient = to[0] || cc[0] || bcc[0];

  const { displayName: primaryRecpientName } = useEmailRecipientDetails(primaryRecipient, { enabled: !!primaryRecipient });

  if (allRecipients.length === 0) return null;

  // Create a list of all other recipients (excluding the one shown)
  const remainingRecipients = allRecipients.filter(
    (r) => r !== primaryRecipient,
  );

  const addOn = remainingRecipients.length > 0 ? (
    ` +${remainingRecipients.length} other${remainingRecipients.length === 1 ? "" : "s"}`
  ) : "";

  return `${primaryRecpientName}${addOn}`;
}
